import React, { useEffect, useLayoutEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Grow,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";

// You can replace this with your actual Physical AI image
import physicalAIImg from "../../../images/physicalAi.png";

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// Styled Components
const BackgroundBox = styled(Box)(({ theme }) => ({
  position: "relative",
  minHeight: "100vh",
  background: "linear-gradient(135deg, #000000 0%, #0a0a0a 50%, #000000 100%)",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    width: "50%",
    height: "50%",
    background: "radial-gradient(circle, rgba(193, 227, 40, 0.15) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "50%",
    height: "50%",
    background: "radial-gradient(circle, rgba(193, 227, 40, 0.15) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const FloatingParticle = styled(Box)(({ delay, duration, left, top }) => ({
  position: "absolute",
  width: 4,
  height: 4,
  borderRadius: "50%",
  background: "rgba(193, 227, 40, 0.3)",
  left: `${left}%`,
  top: `${top}%`,
  animation: `${pulse} ${duration}s ease-in-out infinite`,
  animationDelay: `${delay}s`,
}));

const GlowCard = styled(Card)(({ theme }) => ({
  position: "relative",
  background: "rgba(0, 0, 0, 0.9)",
  backdropFilter: "blur(20px)",
  borderRadius: 24,
  border: "1px solid rgba(193, 227, 40, 0.3)",
  overflow: "hidden",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
  transition: "border-color 0.3s ease",
  "&:hover": {
    borderColor: "rgba(193, 227, 40, 0.4)",
  },
}));

const ProductImage = styled(Box)(({ theme }) => ({
  position: "relative",
  height: 500,
  overflow: "hidden",
  [theme.breakpoints.down('md')]: {
    height: 400,
  },
  [theme.breakpoints.down('sm')]: {
    height: 300,
  },
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: "linear-gradient(135deg, rgba(193, 227, 40, 0.15) 0%, rgba(193, 227, 40, 0.1) 50%, transparent 100%)",
    zIndex: 1,
    opacity: 1,
  },
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
}));


const PhysicalAI = () => {
  useLayoutEffect(() => {
    // Scroll to top when component mounts - useLayoutEffect runs before paint
    // The scrollable container is the .main-content Box in App.js
    const scrollContainer = document.querySelector('.main-content');
    if (scrollContainer) {
      scrollContainer.scrollTo(0, 0);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);
  
  useEffect(() => {
    // Also scroll after a brief delay to ensure it works
    const timer = setTimeout(() => {
      const scrollContainer = document.querySelector('.main-content');
      if (scrollContainer) {
        scrollContainer.scrollTo(0, 0);
      } else {
        window.scrollTo(0, 0);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <BackgroundBox
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 4, sm: 6, md: 10 },
        px: { xs: 2, sm: 4, md: 8 },
      }}
    >
      {/* Floating Particles */}
      {[...Array(20)].map((_, i) => (
        <FloatingParticle
          key={i}
          delay={Math.random() * 3}
          duration={2 + Math.random() * 3}
          left={Math.random() * 100}
          top={Math.random() * 100}
        />
      ))}

      <Container maxWidth="xl">
        {/* Section Header */}
        {/* <Fade in timeout={1000}>
          <Box textAlign="center" mb={8}>
            <Chip
              icon={<FiberManualRecord sx={{ fontSize: 12, animation: `${pulse} 2s ease-in-out infinite` }} />}
              label="COMPUTER VISION & AI SOLUTIONS"
              sx={{
                background: "rgba(193, 227, 40, 0.1)",
                border: "1px solid rgba(193, 227, 40, 0.2)",
                backdropFilter: "blur(8px)",
                color: "#c1e328",
                fontWeight: 600,
                letterSpacing: 1.5,
                mb: 3,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "2.5rem", sm: "3rem", lg: "3.75rem" },
                fontWeight: 700,
                color: "#fff",
                letterSpacing: -1,
                mb: 2,
              }}
            >
                                     AI Vision Processing 
                                     {" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #c1e328 0%, #a0c020 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Intelligence
              </Box>
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "rgba(148, 163, 184, 1)",
                maxWidth: 700,
                mx: "auto",
                lineHeight: 1.8,
                fontSize: { xs: "1rem", md: "1.125rem" },
              }}
            >
              Transform robots, drones, vehicles, cameras etc with our Cutting-edge Computer Vision and AI Solutions.
            </Typography>
          </Box>
        </Fade> */}

        {/* Main Card */}
        <Grow in timeout={1200}>
          <GlowCard>
            <Grid container>
              {/* Content Section - LEFT */}
              <Grid item xs={12} lg={6}>
                <CardContent sx={{ p: { xs: 3, sm: 4, md: 8 }, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <Stack spacing={{ xs: 2.5, sm: 3, md: 4 }}>
                    {/* Title */}
                    <Box>
                    <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "1.5rem", sm: "2.0rem", md: "2.15rem", lg: "3.15rem" },
                fontWeight: 700,
                color: "#fff",
                letterSpacing: { xs: -0.5, md: -1 },
                mb: { xs: 1.5, md: 2 },
              }}
            >
              Add Intelligence to Your{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #c1e328 0%, #a0c020 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Physical Products
              </Box>
            </Typography>
                    </Box>

                    {/* Description */}
                    <Box>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: "rgba(203, 213, 225, 1)", 
                          lineHeight: 1.8,
                          fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" }
                        }}
                      >
              Transform robots, drones, vehicles, cameras etc with our Cutting-edge Computer Vision and AI Solutions.
              </Typography>
                    </Box>

                  </Stack>
                </CardContent>
              </Grid>

              {/* Image Section - RIGHT */}
              <Grid item xs={12} lg={6}>
                <ProductImage>
                  <img src={physicalAIImg} alt="General Intelligence" />
                </ProductImage>
              </Grid>
            </Grid>
          </GlowCard>
        </Grow>

      </Container>
    </BackgroundBox>
  );
};

export default PhysicalAI;